/**
 * Finds the first field (in tanstack registration order, which matches
 * visual/template order for standard forms) that has a non-empty `errors`
 * array on its `fieldMeta`. Focuses its DOM input and scrolls it into
 * view if needed.
 *
 * Every `Form*Field` in this package renders its underlying input with
 * `id={name}`, so `getElementById(fieldName)` reliably finds the element
 * across text, textarea, combobox, select, phone, and date fields.
 *
 * Returns the focused field name (for testing / logging), or `undefined`
 * if no errored field was found.
 *
 * Usage:
 * ```ts
 * const focused = focusFirstErroredField(form)
 * if (focused) console.log(`focused ${focused}`)
 * ```
 *
 * Called internally by `useFormSubmit` on invalid submit; also exported
 * as a standalone helper for forms that need the behavior outside the
 * normal submit flow (e.g. after an async side-channel sets field
 * errors).
 */
export function focusFirstErroredField(form: {
  state: { fieldMeta?: Record<string, { errors?: unknown[] } | undefined> }
}): string | undefined {
  const meta = form.state.fieldMeta
  if (!meta) return undefined
  for (const [name, fieldMeta] of Object.entries(meta)) {
    if (fieldMeta?.errors && fieldMeta.errors.length > 0) {
      const el = document.getElementById(name)
      if (el instanceof HTMLElement) {
        el.focus()
        // `nearest` keeps the scroll minimal — only moves if the field
        // is off-screen — and avoids animated behavior that could cause
        // a perceived focus shift.
        el.scrollIntoView({ block: 'nearest' })
      }
      return name
    }
  }
  return undefined
}
